import React, { useMemo } from 'react';
import { getToneLearningUnitById, getLessonsByUnitId } from '../data/toneLearningData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const ToneUnitDetailPage = ({ unitId, language = 'traditional', onBack, onLessonClick }) => {
  const unitLocal = getToneLearningUnitById(unitId);
  const lessonsLocal = getLessonsByUnitId(unitId);

  const { data: remote } = useSupabaseData({
    table: 'eng_tonelearning',
    mapRow: (row) => row,
    getFallback: () => null,
    query: {
      // eq: { unit_id: unitId }
    }
  });

  const { unit, lessons } = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      return { unit: unitLocal, lessons: lessonsLocal };
    }
    const rows = Array.isArray(remote) ? remote : [remote];
    const outUnit = unitLocal || (rows.find(r => (r.type || r.section) === 'unit') ? {
      id: unitId,
      icon: rows[0]?.icon || '🎵',
      title: { tc: rows[0]?.title_tc || '', sc: rows[0]?.title_sc || '', en: rows[0]?.title_en || '' },
      description: { tc: rows[0]?.description_tc || '', sc: rows[0]?.description_sc || '', en: rows[0]?.description_en || '' },
      duration: rows[0]?.duration || '15 min',
      totalLessons: rows[0]?.total_lessons || 0,
      difficulty: rows[0]?.difficulty || 'beginner',
      topics: rows[0]?.topics || [],
      targetSkills: rows[0]?.target_skills || []
    } : null);
    const outLessons = rows
      .filter(r => (r.type || r.section) === 'lesson' || r.lesson_id)
      .map((r, idx) => ({
        id: r.lesson_id || r.id,
        type: r.type_value || r.type || 'practice',
        typeIcon: r.type_icon || '🎵',
        title: { tc: r.title_tc || '', sc: r.title_sc || '', en: r.title_en || '' },
        content: { theory: { tc: r.theory_tc || '', sc: r.theory_sc || '', en: r.theory_en || '' } },
        difficulty: r.difficulty || 'beginner',
        duration: r.duration || '10 min',
        isUnlocked: r.is_unlocked !== false,
        isCompleted: Boolean(r.is_completed)
      }));
    return { unit: outUnit, lessons: outLessons.length > 0 ? outLessons : lessonsLocal };
  }, [remote, unitId]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const getLessonTypeIcon = (type, typeIcon) => {
    return typeIcon || '📚';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500/20 text-green-300';
      case 'intermediate':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'advanced':
        return 'bg-red-500/20 text-red-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  if (!unit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <div className="px-6 py-8">
          <button onClick={onBack} className="text-blue-400 mb-4">
            ← {language === 'traditional' ? '返回' : language === 'simplified' ? '返回' : 'Back'}
          </button>
          <div className="text-center py-8">
            <p>{language === 'traditional' ? '單元未找到' : language === 'simplified' ? '单元未找到' : 'Unit not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            ←
          </button>
          <h1 className="text-xl font-bold">
            {getText(unit.title)}
          </h1>
          <div className="w-8"></div> {/* Spacer for centering */}
        </div>

        {/* Unit Info Card */}
        <div className="bg-gray-800/30 border border-gray-600 rounded-xl p-6 mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="text-4xl">
              {unit.icon}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-1">
                {getText(unit.title)}
              </h2>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span>⏱️ {unit.duration}</span>
                <span>📚 {unit.totalLessons} {language === 'traditional' ? '課程' : language === 'simplified' ? '课程' : 'lessons'}</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(unit.difficulty)}`}>
                  {unit.difficulty.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <p className="text-gray-300 mb-6">
            {getText(unit.description)}
          </p>

          {/* Topics */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3">
              {language === 'traditional' ? '學習主題' :
               language === 'simplified' ? '学习主题' :
               'Learning Topics'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {unit.topics.map((topic, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>

          {/* Target Skills */}
          <div>
            <h3 className="font-semibold mb-3">
              {language === 'traditional' ? '目標技能' :
               language === 'simplified' ? '目标技能' :
               'Target Skills'}
            </h3>
            <div className="space-y-2">
              {unit.targetSkills.map((skill, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 text-gray-300 text-sm"
                >
                  <span className="text-green-400">✓</span>
                  <span>{skill}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Lessons List */}
        <div className="space-y-3 mb-20">
          <h3 className="text-lg font-semibold mb-4">
            {language === 'traditional' ? '課程列表' :
             language === 'simplified' ? '课程列表' :
             'Lesson List'}
          </h3>
          
          {lessons.map((lesson) => (
            <div
              key={lesson.id}
              onClick={() => lesson.isUnlocked && onLessonClick(lesson.id)}
              className={`bg-gray-800/30 border border-gray-600 rounded-xl p-4 cursor-pointer transition-all ${
                lesson.isUnlocked 
                  ? 'hover:bg-gray-700/50 hover:border-gray-500' 
                  : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center space-x-4">
                {/* Lesson Icon */}
                <div className="text-2xl flex-shrink-0">
                  {getLessonTypeIcon(lesson.type, lesson.typeIcon)}
                </div>
                
                {/* Lesson Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-semibold text-white">
                      {getText(lesson.title)}
                    </h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(lesson.difficulty)}`}>
                      {lesson.difficulty.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-gray-400 text-sm">
                    {getText(lesson.content.theory)}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                    <span>⏱️ {lesson.duration}</span>
                    <span>📚 {lesson.type.toUpperCase()}</span>
                  </div>
                </div>
                
                {/* Status */}
                <div className="flex flex-col items-end">
                  {lesson.isUnlocked ? (
                    lesson.isCompleted ? (
                      <div className="text-green-400 text-sm">✓ {language === 'traditional' ? '已完成' : language === 'simplified' ? '已完成' : 'Completed'}</div>
                    ) : (
                      <div className="text-blue-400 text-sm">→ {language === 'traditional' ? '開始' : language === 'simplified' ? '开始' : 'Start'}</div>
                    )
                  ) : (
                    <div className="text-gray-500 text-sm">🔒 {language === 'traditional' ? '鎖定' : language === 'simplified' ? '锁定' : 'Locked'}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ToneUnitDetailPage;
